import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { AppState } from '@nxsys/store';
import { dashboardActions } from '@nxsys/store/slices/dashboard/reducer/dashboardSlice';
import { GetAgencyPaymentListParams } from '@nxsys/store/slices/dashboard/types';
import { Invoice } from '@nxsys/types/models/invoice';
import { showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export enum AgencyPaymentModalTypeEnum {
  TOTAL = 'TOTAL',
  OUTSTANDING = 'OUTSTANDING',
  CREDIT_NOTE = 'CREDIT_NOTE',
}

type Props = {
  isOpen: boolean;
  toggle: () => void;
  type: AgencyPaymentModalTypeEnum;
  agencyId: string;
};

const AgencyPaymentModal: React.FC<Props> = ({
  isOpen,
  toggle,
  type,
  agencyId,
}) => {
  const dispatch = useDispatch();
  const { agencyPaymentList } = useSelector(
    (state: AppState) => state.dashboardStore,
  );
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const { list, pagination, loading, queryParams } = agencyPaymentList;

  useEffect(() => {
    if (isOpen)
      handleFetchData({
        page: 0,
        size: 20,
      });
  }, [isOpen]);

  const handleFetchData = (params: GetAgencyPaymentListParams) => {
    const newParams = {
      ...params,
      agencyIds: [agencyId],
      agencyRefs: [agencyId],
    };
    if (type === AgencyPaymentModalTypeEnum.OUTSTANDING) {
      newParams.isOutstanding = true;
    } else if (type === AgencyPaymentModalTypeEnum.CREDIT_NOTE) {
      newParams.isCreditNote = true;
    }
    dispatch(dashboardActions.getAgencyPaymentList(newParams));
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const invoiceColumns: ColumnsType<Invoice> = [
    {
      title: 'Invoice No',
      dataIndex: 'invoiceNo',
    },
    {
      key: 'invoicedDate',
      title: 'Date',
      align: 'right',
      dataIndex: 'invoicedDate',
      render: (value) =>
        value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '_',
      width: 140,
    },
    {
      key: 'taxPeriod',
      title: 'Tax Period',
      align: 'right',
      render: (record) =>
        `${record?.taxPeriod || ''}/${
          record?.taxYear || record?.currentYear || ''
        }`,
    },
    {
      title: 'Agency Ref',
      dataIndex: 'subcontractorRef',
      render: (value) =>
        `${value?.primaryAgency?.agencyPrefix || '_'}/${
          value?.agencyRef || value?.systemId || '_'
        }`,
    },
    {
      title: 'Forename',
      dataIndex: 'subcontractorRef',
      render: (value) => value?.forename || '',
    },
    {
      title: 'Surname',
      dataIndex: 'subcontractorRef',
      render: (value) => value?.surname || '',
    },
    {
      key: 'setting',
      title: 'UTR No',
      dataIndex: 'setting',
      render: (value) => value?.UTRNo || '',
    },
  ];

  const creditNoteColumns: ColumnsType<Invoice> = [
    {
      title: 'CRN No',
      dataIndex: 'creditNo',
    },
    {
      key: 'creditDate',
      title: 'Date',
      align: 'right',
      dataIndex: 'creditDate',
      render: (value) =>
        value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '_',
      width: 140,
    },
    {
      key: 'companyRef',
      title: 'From',
      dataIndex: 'companyRef',
      render: (value) => value?.companyName || '-',
    },
    {
      key: 'to',
      title: 'To',
      dataIndex: 'agencyRef',
      render: (value) => value?.name || '-',
    },
    {
      key: 'totalNet',
      title: 'Net',
      dataIndex: 'totalNet',
      align: 'right',
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'totalVat',
      title: 'VAT',
      dataIndex: 'totalVat',
      align: 'right',
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'totalPrices',
      title: 'Total',
      dataIndex: 'totalPrices',
      align: 'right',
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },
  ];

  const columns = useMemo(
    () =>
      type === AgencyPaymentModalTypeEnum.CREDIT_NOTE
        ? creditNoteColumns
        : invoiceColumns,
    [type, payrollCountry],
  );

  return (
    <AppModalContainer
      title={
        <div className="flex items-center gap-x-[8px]">
          <div>
            {type === AgencyPaymentModalTypeEnum.OUTSTANDING
              ? 'Outstanding Invoices'
              : type === AgencyPaymentModalTypeEnum.CREDIT_NOTE
                ? 'Credit Notes'
                : 'Total Invoices'}
          </div>
          <div className="px-[12px] py-[2px] rounded-full bg-primary-50 border border-primary-500 text-primary-600 text-[14px]">
            {list?.length}
          </div>
        </div>
      }
      isOpen={isOpen}
      toggle={toggle}
      hiddenFooter
      className="max-sm:w-[100vw]"
      width={1200}
      minHeight="0"
      noPaddingBody
    >
      <AppsContent>
        <AppTableContainer
          rowKey="id"
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 1200 }}
          columns={columns}
          onChangePagination={onChangePagination}
        />
      </AppsContent>
    </AppModalContainer>
  );
};

export default AgencyPaymentModal;
